// ExpensesPage.js - Main Component
import React, {useEffect, useState} from "react";
import CryptoJS from "crypto-js";
import {styled} from "@mui/material/styles";
import {useMediaQuery, useTheme} from "@mui/material";
import {ApiDetails, KEY} from "../../Constants";

// Component imports
import ExpenseHeader from "./components/ExpenseHeader";
import ExpenseFilters from "./components/ExpenseFilters";
import ExpenseDataGrid from "./components/ExpenseDataGrid";
import ExpenseSummary from "./components/ExpenseSummary";
import AddExpenseModal from "./components/modals/AddExpenseModal";
import ExpenseDetailsModal from "./components/modals/ExpenseDetailsModal";
import LoadingBackdrop from "./components/LoadingBackdrop";

// Material UI Components
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { handle401Unauthorized, handle403Forbidden } from '../../utils/authErrorHandler';


const MainContainer = styled(Box)(({theme}) => ({
   /* padding: theme.spacing(3),
    backgroundColor: '#f9fafc',
    borderRadius: theme.spacing(1),
    minHeight: 'calc(100vh - 100px)',*/
    zoom: "80%",
}));

const ContentPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
}));

function ExpensesPage(props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Decrypt localStorage data
    let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
        localStorage.getItem("myMap"),
        KEY
    );
    let portfolioDetailsDecrypted = CryptoJS.AES.decrypt(
        localStorage.portfolioDetails,
        KEY
    );
    let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
    let originalText2 = portfolioDetailsDecrypted.toString(CryptoJS.enc.Utf8);

    // State - Loading
    const [isLoading, setIsLoading] = useState(true);

    // State - Data
    const [expenses, setExpenses] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [properties, setProperties] = useState([]);

    // State - Filters
    const [dateFrom, setDateFrom] = useState(new Date("1999-08-04"));
    const [dateTo, setDateTo] = useState(new Date());
    const [searchTerm, setSearchTerm] = useState("");

    // State - Modals
    const [addExpenseModal, setAddExpenseModal] = useState(false);
    const [expenseDetailsModal, setExpenseDetailsModal] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);

    // State - For adding new expense
    const [expenseDate, setExpenseDate] = useState(new Date());
    const [propertyId, setPropertyId] = useState("");
    const [propertyName, setPropertyName] = useState("");
    const [units, setUnits] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [selectedExpenseType, setSelectedExpenseType] = useState("");
    const [description, setDescription] = useState("");

    // Stats - Summary data for expense dashboard
    const [expenseStats, setExpenseStats] = useState({
        totalExpenses: 0,
        totalAmount: 0,
        byCategory: {},
        byProperty: {},
        monthlyData: []
    });

    // Fetch initial data
    useEffect(() => {
        props.refreshSideBar();
        fetchExpenses();
    }, []);

    // Calculate expense statistics whenever expenses change
    useEffect(() => {
        if (expenses.length > 0) {
            calculateExpenseStats();
        }
    }, [expenses]);

    // Calculate expense statistics
    const calculateExpenseStats = () => {
        const totalAmount = expenses.reduce((sum, expense) => sum + parseFloat(expense.cost), 0);

        // Group by category
        const byCategory = {};
        expenses.forEach(expense => {
            const type = getExpenseTypeName(expense.expense_type);
            byCategory[type] = (byCategory[type] || 0) + parseFloat(expense.cost);
        });

        // Group by property
        const byProperty = {};
        expenses.forEach(expense => {
            const propertyName = getPropertyNameById(expense.property_id);
            byProperty[propertyName] = (byProperty[propertyName] || 0) + parseFloat(expense.cost);
        });

        // Monthly data
        const monthlyData = [];
        const lastSixMonths = getLastSixMonths();

        lastSixMonths.forEach(month => {
            const monthExpenses = expenses.filter(expense => {
                const expenseDate = new Date(expense.date_created);
                return expenseDate.getMonth() === month.monthIndex &&
                    expenseDate.getFullYear() === month.year;
            });

            const totalForMonth = monthExpenses.reduce(
                (sum, expense) => sum + parseFloat(expense.cost), 0
            );

            monthlyData.push({
                month: month.name,
                total: totalForMonth
            });
        });

        setExpenseStats({
            totalExpenses: expenses.length,
            totalAmount,
            byCategory,
            byProperty,
            monthlyData
        });
    };

    // Get last six months for chart
    const getLastSixMonths = () => {
        const months = [];
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        const today = new Date();
        let currentMonth = today.getMonth();
        let currentYear = today.getFullYear();

        for (let i = 0; i < 6; i++) {
            months.unshift({
                name: monthNames[currentMonth],
                monthIndex: currentMonth,
                year: currentYear
            });

            currentMonth--;
            if (currentMonth < 0) {
                currentMonth = 11;
                currentYear--;
            }
        }

        return months;
    };

    // Fetch expenses data
    const fetchExpenses = () => {
        setIsLoading(true);

        fetch(ApiDetails + "pegasus/visionary/expenses/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify({
                landlord_id: JSON.parse(originalText)[0][1],
            }),
        })
            .then(async (response) => {
                let expenses = await response.json();
                 // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                    // Fetch expense types
                    fetch(ApiDetails + "pegasus/visionary/expenseTypes/", {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                        },
                    })
                        .then(async (response) => {
                            let expenseTypes = await response.json();
                             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                                setExpenseTypes(expenseTypes);
                                setExpenses(expenses);
                                setSearchResults(expenses);

                                // Extract properties from localStorage
                                const propertiesData = new Map(JSON.parse(originalText2)).get("properties") || [];
                                setProperties(propertiesData);

                                setIsLoading(false);
                            } else {
                                props.snackBar({
                                    text: "Failed to fetch expense types",
                                    type: "error",
                                });
                                setIsLoading(false);
                            }
                        })
                        .catch((err) => {
                            console.log(err);
                            props.snackBar({
                                text: "Something Went Wrong. Call Customer Care For Assistance.",
                                type: "error",
                            });
                            setIsLoading(false);
                        });
                } else {
                    props.snackBar({
                        text: "Failed to fetch expenses",
                        type: "error",
                    });
                    setIsLoading(false);
                }
            })
            .catch((err) => {
                console.log(err);
                props.snackBar({
                    text: "Something Went Wrong. Call Customer Care For Assistance.",
                    type: "error",
                });
                setIsLoading(false);
            });
    };

    // Get property units
    const getUnits = (propertyId) => {
        fetch(ApiDetails + "pegasus/visionary/property/getUnits", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify({property_id: propertyId}),
        })
            .then(async (response) => {
                let data = await response.json();
                 // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                    setUnits(data);
                    props.snackBar({
                        text: "Property Units Retrieved Successfully",
                        type: "success",
                    });
                } else {
                    props.snackBar({
                        text: "Failed to retrieve property units",
                        type: "error",
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                props.snackBar({
                    text: "Something Went Wrong. Try Again Later",
                    type: "error",
                });
            });
    };

    // Add expense
    const addExpense = (values) => {

        console.log("ADD EXPENSE MODAL VALUES")
        console.log(values)

        if (!description || description.trim() === "") {
            props.snackBar({
                text: "Description cannot be empty",
                type: "error",
            });
            return;
        }

        if (!propertyId || propertyId.trim() === "") {
            props.snackBar({
                text: "Please select a property",
                type: "error",
            });
            return;
        }

        if (!selectedExpenseType || selectedExpenseType.trim() === "") {
            props.snackBar({
                text: "Please select an expense type",
                type: "error",
            });
            return;
        }

        if (!values.amount || values.amount <= 0) {
            props.snackBar({
                text: "Please enter a valid amount",
                type: "error",
            });
            return;
        }

        const body = {
            description: description,
            property_id: propertyId,
            expense_type: selectedExpenseType,
            house_number: selectedUnit || "",
            cost: values.amount,
            landlord_id: JSON.parse(originalText)[0][1],
            year: expenseDate.getFullYear(),
            month: expenseDate.getMonth(),
            day: expenseDate.getDate(),
        };

        fetch(ApiDetails + "pegasus/visionary/expenses/add", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify(body),
        })
            .then(async (response) => {
                 // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                    props.snackBar({
                        text: "Expense Added Successfully",
                        type: "success",
                    });
                    fetchExpenses();
                    handleCloseModal();

                    // Reset form fields
                    setSelectedUnit(null);
                    setDescription("");
                    setPropertyId("");
                    setPropertyName("");
                    setSelectedExpenseType("");
                } else {
                    props.snackBar({
                        text: "Failed to add expense",
                        type: "error",
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                props.snackBar({
                    text: "Something Went Wrong. Call Customer Care For Assistance.",
                    type: "error",
                });
            });
    };

    // Delete expense
    const deleteExpense = (id) => {
        fetch(ApiDetails + "pegasus/visionary/expenses/delete", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify({expense_id: id}),
        })
            .then(async (response) => {
                 // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                    props.snackBar({
                        text: "Expense Deleted Successfully",
                        type: "success",
                    });
                    fetchExpenses();
                } else {
                    props.snackBar({
                        text: "Failed to delete expense",
                        type: "error",
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                props.snackBar({
                    text: "Something Went Wrong. Call Customer Care For Assistance.",
                    type: "error",
                });
            });
    };

    // Search expenses by term
    const handleSearch = (term) => {
        setSearchTerm(term);

        if (!term) {
            // If search term is empty, apply date filter only
            handleDateFilter(dateFrom, dateTo);
            return;
        }

        // Apply both term and date filters
        const termFiltered = expenses.filter(expense =>
            expense.description.toLowerCase().includes(term.toLowerCase())
        );

        const dateFiltered = filterByDateRange(termFiltered, dateFrom, dateTo);
        setSearchResults(dateFiltered);
    };

    // Filter by date range
    const handleDateFilter = (from, to) => {
        setDateFrom(from);
        setDateTo(to);

        // Apply both term and date filters
        let filtered = expenses;

        if (searchTerm) {
            filtered = filtered.filter(expense =>
                expense.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setSearchResults(filterByDateRange(filtered, from, to));
    };

    // Filter helper function
    const filterByDateRange = (expenses, from, to) => {
        return expenses.filter(expense => {
            const expenseDate = new Date(expense.date_created);
            return expenseDate >= from && expenseDate <= to;
        });
    };

    // Helper to get expense type name
    const getExpenseTypeName = (typeId) => {
        const expenseType = expenseTypes.find(type => type.id === typeId);
        return expenseType ? expenseType.type : "Unknown";
    };

    // Helper to get property name
    const getPropertyNameById = (propertyId) => {
        const property = properties.find(p => p.id === propertyId);
        return property ? property.property_name : "Unknown Property";
    };

    // Modal handlers
    const handleOpenAddModal = () => {
        setAddExpenseModal(true);
    };

    const handleOpenDetailsModal = (expense) => {
        // Prepare expense details with property name and expense type
        const propertyName = getPropertyNameById(expense.property_id);
        const expenseType = getExpenseTypeName(expense.expense_type);

        setSelectedExpense({
            ...expense,
            property_name: propertyName,
            expense_type_name: expenseType
        });

        setExpenseDetailsModal(true);
    };

    const handleCloseModal = () => {
        setAddExpenseModal(false);
        setExpenseDetailsModal(false);
        setSelectedExpense(null);
    };

    // Property selection handler
    const handlePropertySelect = (propertyId) => {
        setPropertyId(propertyId);

        // Get property name
        const selectedProperty = properties.find(p => p.id === propertyId);
        if (selectedProperty) {
            setPropertyName(selectedProperty.property_name);
        }

        // Get units for the selected property
        getUnits(propertyId);
    };

    return (
        <MainContainer>
            {isLoading && <LoadingBackdrop/>}

            {/* Modals */}
            <AddExpenseModal
                open={addExpenseModal}
                onClose={handleCloseModal}
                expenseDate={expenseDate}
                setExpenseDate={setExpenseDate}
                properties={properties}
                propertyId={propertyId}
                selectedUnit={selectedUnit}
                setSelectedUnit={setSelectedUnit}
                units={units}
                fetchExpenses={fetchExpenses}
                landlordId={JSON.parse(originalText)[0][1]}
                expenseTypes={expenseTypes}
                selectedExpenseType={selectedExpenseType}
                setSelectedExpenseType={setSelectedExpenseType}
                description={description}
                setDescription={setDescription}
                snackBar={props.snackbar}
                onPropertySelect={handlePropertySelect}
                onSubmit={addExpense}
            />

            <ExpenseDetailsModal
                open={expenseDetailsModal}
                onClose={handleCloseModal}
                expense={selectedExpense}
            />

            {/* Page Header */}
            <ExpenseHeader
                totalExpenses={expenseStats.totalExpenses}
                totalAmount={expenseStats.totalAmount}
                onAddExpense={handleOpenAddModal}
                isMobile={isMobile}
            />

            {/* Expense Summary */}
            <ExpenseSummary
                stats={expenseStats}
                isMobile={isMobile}
            />

            {/* Filters */}
            <ContentPaper sx={{mb: 3}}>
                <ExpenseFilters
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    searchTerm={searchTerm}
                    onDateChange={handleDateFilter}
                    onSearch={handleSearch}
                    onAddExpense={handleOpenAddModal}
                    isMobile={isMobile}
                />
            </ContentPaper>

            {/* Data Grid */}
            <ContentPaper>
                <ExpenseDataGrid
                    expenses={searchResults}
                    expenseTypes={expenseTypes}
                    properties={properties}
                    onViewDetails={handleOpenDetailsModal}
                    onDeleteExpense={deleteExpense}
                    getExpenseTypeName={getExpenseTypeName}
                    getPropertyName={getPropertyNameById}
                />
            </ContentPaper>
        </MainContainer>
    );
}

export default ExpensesPage;